import { useState, useEffect } from 'react'
import { supabase } from '../lib/supabase'

interface AuthModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAuthenticate: () => void;
}

export default function AuthModal({ isOpen, onClose, onAuthenticate }: AuthModalProps) {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [isEmailLoading, setIsEmailLoading] = useState(false)
  const [isResending, setIsResending] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [emailSent, setEmailSent] = useState(false)
  const [resendSuccess, setResendSuccess] = useState(false)

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = 'unset'
    }
    return () => {
      document.body.style.overflow = 'unset'
    }
  }, [isOpen])

  if (!isOpen) return null

  const handleGoogleAuth = async () => {
    setIsLoading(true)
    setError(null)
    
    try {
      // Check user limit before OAuth
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
      const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY!
      const checkLimitResponse = await fetch(`${supabaseUrl}/functions/v1/check-user-limit`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${supabaseAnonKey}`,
          'apikey': supabaseAnonKey,
          'Content-Type': 'application/json'
        }
      })
      
      if (!checkLimitResponse.ok) {
        const errorText = await checkLimitResponse.text()
        console.error('Error checking user limit:', checkLimitResponse.status, errorText)
        // Block signup if limit check fails (fail closed for security)
        setError('Impossible de vérifier la limite d\'utilisateurs. Veuillez réessayer plus tard.')
        setIsLoading(false)
        return
      }
      
      const limitData = await checkLimitResponse.json()
      
      if (!limitData.canSignUp) {
        setError('La limite de 100 utilisateurs a été atteinte. Veuillez réessayer plus tard.')
        setIsLoading(false)
        return
      }
      
      // Proceed with OAuth
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: window.location.origin
          // Note: We don't request Google Sheets scope here to allow all users to sign in
          // The scope will be requested when user tries to export to Google Sheets
        }
      })
      
      if (error) {
        // Provide user-friendly error messages
        if (error.message.includes('access_denied') || error.message.includes('testing')) {
          setError('L\'application Google OAuth est en mode test. Veuillez contacter l\'administrateur pour obtenir l\'accès, ou attendez que l\'application soit publiée.')
        } else {
          setError(error.message)
        }
        setIsLoading(false)
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Une erreur est survenue lors de la connexion')
      setIsLoading(false)
    }
  }

  const handleEmailContinue = async (e: React.FormEvent) => {
    e.preventDefault()
    
    // If password field is not shown yet, just show it
    if (!showPassword) {
      setShowPassword(true)
      return
    }
    
    // If password is shown, proceed with sign up
    setIsEmailLoading(true)
    setError(null)
    
    try {
      // Check user limit before signup
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
      const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY!
      const checkLimitResponse = await fetch(`${supabaseUrl}/functions/v1/check-user-limit`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${supabaseAnonKey}`,
          'apikey': supabaseAnonKey,
          'Content-Type': 'application/json'
        }
      })
      
      if (!checkLimitResponse.ok) {
        const errorText = await checkLimitResponse.text()
        console.error('Error checking user limit:', checkLimitResponse.status, errorText)
        // Block signup if limit check fails (fail closed for security)
        setError('Impossible de vérifier la limite d\'utilisateurs. Veuillez réessayer plus tard.')
        setIsEmailLoading(false)
        return
      }
      
      const limitData = await checkLimitResponse.json()
      
      if (!limitData.canSignUp) {
        throw new Error('La limite de 100 utilisateurs a été atteinte. Veuillez réessayer plus tard.')
      }
      
      // Proceed with signup
      const { error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: window.location.origin + '/auth/callback'
        }
      })
      
      if (error) throw error
      
      setEmailSent(true)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Une erreur est survenue')
    } finally {
      setIsEmailLoading(false)
    }
  }

  const handleResendEmail = async () => {
    setIsResending(true)
    setResendSuccess(false)
    setError(null)
    
    try {
      const { error } = await supabase.auth.resend({
        type: 'signup',
        email,
        options: {
          emailRedirectTo: window.location.origin + '/auth/callback'
        }
      })
      
      if (error) throw error
      
      setResendSuccess(true)
      setTimeout(() => setResendSuccess(false), 3000)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Une erreur est survenue lors du renvoi')
    } finally {
      setIsResending(false)
    }
  }

  const handleResetForm = () => {
    setEmail('')
    setPassword('')
    setShowPassword(false)
    setEmailSent(false)
    setError(null)
    setResendSuccess(false)
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/50 backdrop-blur-[2px]"
        onClick={onClose}
      />

      {/* Modal - ChatGPT style dimensions */}
      <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-[440px] max-h-[80vh] flex flex-col">
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 transition-colors z-10"
          aria-label="Fermer"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        {/* Scrollable content */}
        <div className="overflow-y-auto overscroll-contain">
          <div className="px-8 pt-10 pb-8">
            {/* Header - Logo always visible */}
            <div className="text-center mb-6">
              {/* Logo */}
              <img 
                src="/logo_supabase_green.png" 
                alt="Avocat AI" 
                className="h-16 w-auto mx-auto mb-4"
              />
              
              {/* Only show title/subtitle on login form, not on email sent screen */}
              {!emailSent && (
                <>
                  <h2 className="text-xl font-semibold text-gray-900 mb-2">
                    Accédez à Avocat AI
                  </h2>
                  <p className="text-sm text-gray-600 leading-snug max-w-sm mx-auto">
                    Gagnez du temps sur vos tâches juridiques.<br />
                    Analysez, rédigez et automatisez vos documents grâce à l'IA
                  </p>
                </>
              )}
            </div>

            {/* Error Message - only show on login form */}
            {error && !emailSent && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-600">
                {error}
              </div>
            )}

            {/* Google Button - only show on login form */}
            {!emailSent && (
              <div className="mb-4">
                <button
                  onClick={handleGoogleAuth}
                  type="button"
                  disabled={isLoading}
                  className="w-full flex items-center justify-center gap-3 px-4 py-3 bg-white border border-gray-300 rounded-full hover:bg-gray-50 hover:shadow-sm transition-all font-medium text-[15px] text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isLoading ? (
                    <svg className="w-[18px] h-[18px] animate-spin" viewBox="0 0 24 24" fill="none">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                  ) : (
                    <svg className="w-[18px] h-[18px]" viewBox="0 0 24 24">
                      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                    </svg>
                  )}
                  <span>Continuer avec Google</span>
                </button>
              </div>
            )}

            {/* Divider - only show on login form */}
            {!emailSent && (
              <div className="relative my-5">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-200"></div>
                </div>
                <div className="relative flex justify-center text-xs">
                  <span className="px-3 bg-white text-gray-500 font-medium uppercase tracking-wide">OU</span>
                </div>
              </div>
            )}

            {/* Email Form */}
            {emailSent ? (
              <div className="text-center">
                {/* Title */}
                <h2 className="text-xl font-semibold text-gray-900 mb-3">
                  Consultez votre boîte de réception
                </h2>

                {/* Body text */}
                <p className="text-sm text-gray-600 leading-relaxed mb-6">
                  Cliquez sur le lien que nous avons envoyé à l'adresse{' '}
                  <span className="font-semibold text-gray-900">{email}</span>{' '}
                  pour finaliser la configuration de votre compte.
                </p>

                {/* Success message for resend */}
                {resendSuccess && (
                  <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg text-sm text-green-700">
                    E-mail renvoyé avec succès !
                  </div>
                )}

                {/* Error message */}
                {error && (
                  <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-600">
                    {error}
                  </div>
                )}

                {/* Primary button - Open Gmail */}
                <a
                  href="https://mail.google.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full py-3 px-4 bg-[#3ECF8E] text-white font-medium text-[15px] rounded-lg hover:bg-[#35b87d] hover:shadow-md transition-all mb-4 flex items-center justify-center gap-2"
                >
                  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M24 5.457v13.909c0 .904-.732 1.636-1.636 1.636h-3.819V11.73L12 16.64l-6.545-4.91v9.273H1.636A1.636 1.636 0 0 1 0 19.366V5.457c0-2.023 2.309-3.178 3.927-1.964L5.455 4.64 12 9.548l6.545-4.91 1.528-1.145C21.69 2.28 24 3.434 24 5.457z"/>
                  </svg>
                  <span>Ouvrir Gmail</span>
                </a>

                {/* Helper text - Resend email */}
                <p className="text-sm text-gray-600 mb-3">
                  Vous ne trouvez aucun e-mail dans votre boîte de réception ou votre dossier des spams ?{' '}
                  <button
                    onClick={handleResendEmail}
                    disabled={isResending}
                    className="text-[#3ECF8E] hover:underline font-medium disabled:opacity-50"
                  >
                    {isResending ? 'Envoi en cours...' : 'Envoyons-en un autre'}
                  </button>
                </p>

                {/* Footer text - Change email */}
                <p className="text-sm text-gray-500">
                  Mauvaise adresse ?{' '}
                  <button
                    onClick={handleResetForm}
                    className="text-[#3ECF8E] hover:underline font-medium"
                  >
                    Déconnexion
                  </button>
                  {' '}pour vous connecter avec une autre adresse e-mail.
                </p>
              </div>
            ) : (
              <form onSubmit={handleEmailContinue} className="space-y-3">
                <input
                  type="email"
                  id="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Saisissez votre e-mail"
                  required
                  disabled={isEmailLoading}
                  className="w-full px-4 py-3 border border-gray-300 rounded-full focus:ring-2 focus:ring-[#3ECF8E] focus:border-transparent outline-none transition-all text-[15px] text-gray-900 placeholder-gray-400 bg-white disabled:opacity-50"
                />

                {showPassword && (
                  <input
                    type="password"
                    id="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Saisissez votre mot de passe"
                    required
                    disabled={isEmailLoading}
                    minLength={6}
                    className="w-full px-4 py-3 border border-gray-300 rounded-full focus:ring-2 focus:ring-[#3ECF8E] focus:border-transparent outline-none transition-all text-[15px] text-gray-900 placeholder-gray-400 bg-white disabled:opacity-50"
                  />
                )}

                <button
                  type="submit"
                  disabled={isEmailLoading}
                  className="w-full py-3 px-4 bg-[#3ECF8E] text-white font-medium text-[15px] rounded-full hover:bg-[#35b87d] hover:shadow-sm transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {isEmailLoading ? (
                    <>
                      <svg className="w-[18px] h-[18px] animate-spin" viewBox="0 0 24 24" fill="none">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      <span>Inscription en cours...</span>
                    </>
                  ) : (
                    <span>{showPassword ? "S'inscrire" : "Continuer avec l'e-mail"}</span>
                  )}
                </button>
              </form>
            )}

            {/* Terms - only show on login form */}
            {!emailSent && (
              <p className="mt-4 text-xs text-center text-gray-500 leading-relaxed">
                En continuant, vous acceptez nos{' '}
                <a href="/terms" className="text-[#0F3D2E] hover:underline font-medium">
                  Conditions d'utilisation
                </a>
                {' '}et notre{' '}
                <a href="/privacy" className="text-[#0F3D2E] hover:underline font-medium">
                  Politique de confidentialité
                </a>
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
